import { useEffect } from "react";

// Define the window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}

/**
 * Options for configuring GTM
 */
interface GTMOptions {
  // User identification options
  userId?: string;
  userProperties?: Record<string, any>;

  // Debug mode
  debug?: boolean;
}

/**
 * A custom hook to initialize and configure Google Tag Manager
 *
 * @param gtmId - Your Google Tag Manager container ID
 * @param options - Configuration options for GTM
 */
const useGTM = (gtmId: string, options?: GTMOptions) => {
  useEffect(() => {
    if (!gtmId) {
      console.warn("GTM ID is missing");
      return;
    }

    try {
      // Initialize dataLayer if it doesn't exist
      window.dataLayer = window.dataLayer || [];

      // Set debug mode if specified
      if (options?.debug) {
        window.dataLayer.push({ "gtm.debug": true });
      }

      // Set user ID if provided
      if (options?.userId) {
        window.dataLayer.push({
          userId: options.userId,
          event: "userIdentified",
        });
        console.log("GTM user identified:", options.userId);
      }

      // Set user properties if provided
      if (
        options?.userProperties &&
        Object.keys(options.userProperties).length > 0
      ) {
        window.dataLayer.push({
          user_properties: options.userProperties,
          event: "userPropertiesSet",
        });
        console.log("GTM user properties set:", options.userProperties);
      }

      console.log("Google Tag Manager initialized successfully");
    } catch (error) {
      console.error("Error initializing Google Tag Manager:", error);
    }
  }, [gtmId, options]);
};

/**
 * Track event in Google Tag Manager
 * Can be used anywhere in the application
 *
 * @param eventName - Name of the event to track
 * @param eventData - Optional data to associate with the event
 */
export const trackEvent = (
  eventName: string,
  eventData?: Record<string, any>
): void => {
  try {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    // Push the event to the dataLayer
    window.dataLayer.push({
      event: eventName,
      ...eventData,
    });

    console.log(`GTM event tracked: ${eventName}`, eventData || "");
  } catch (error) {
    console.error(`Error tracking GTM event ${eventName}:`, error);
  }
};

/**
 * Track e-commerce events in Google Tag Manager
 * Following the GA4 e-commerce event structure
 */
export const trackEcommerceEvent = (
  eventName: string,
  ecommerceData: Record<string, any>
): void => {
  try {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    // Push the e-commerce event to the dataLayer
    window.dataLayer.push({
      event: eventName,
      ecommerce: {
        ...ecommerceData,
      },
    });

    console.log(`GTM e-commerce event tracked: ${eventName}`, ecommerceData);
  } catch (error) {
    console.error(`Error tracking GTM e-commerce event ${eventName}:`, error);
  }
};

/**
 * Track form submission events
 */
export const trackFormSubmission = (
  formId: string,
  formName: string,
  formData?: Record<string, any>
): void => {
  try {
    trackEvent("form_submission", {
      form_id: formId,
      form_name: formName,
      form_data: formData ? JSON.stringify(formData) : undefined,
    });
  } catch (error) {
    console.error(`Error tracking form submission for ${formName}:`, error);
  }
};

/**
 * Track blog-specific interactions
 */
export const trackBlogInteraction = (
  interactionType: "view" | "read" | "share" | "comment" | "search",
  blogData: {
    article_id?: string;
    article_title?: string;
    article_category?: string;
    article_tags?: string[];
    search_term?: string;
    time_spent?: number;
    scroll_depth?: number;
  }
): void => {
  try {
    trackEvent(`blog_${interactionType}`, {
      ...blogData,
      blog_section: true,
    });
  } catch (error) {
    console.error(`Error tracking blog interaction ${interactionType}:`, error);
  }
};

/**
 * Track product customization events
 */
export const trackProductCustomization = (
  productId: string,
  productName: string,
  customizationType: string,
  customizationValue: any,
  customizationPrice?: number
): void => {
  try {
    trackEvent("product_customization", {
      item_id: productId,
      item_name: productName,
      customization_type: customizationType,
      customization_value:
        typeof customizationValue === "object"
          ? JSON.stringify(customizationValue)
          : String(customizationValue),
      customization_price: customizationPrice || 0,
    });
  } catch (error) {
    console.error(`Error tracking product customization:`, error);
  }
};

export default useGTM;
